import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CompanyTable from "../components/CompanyTable";
import { fetchScreener, fetchSectorList } from "../lib/api";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const DIMENSIONS = [
  { v: "composite", label: "Composite" },
  { v: "customer", label: "Customer" },
  { v: "geographic", label: "Geographic" },
  { v: "supplier", label: "Supplier" },
  { v: "product", label: "Product" },
  { v: "credit", label: "Credit" },
];
const MINPCTS = [0, 10, 15, 20, 30, 50];
const RISKS = [
  { v: "all", label: "All risk levels" },
  { v: "high", label: "High" },
  { v: "medium", label: "Medium" },
  { v: "low", label: "Low" },
];

export default function Screener() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [sectorList, setSectorList] = useState([]);
  const [loading, setLoading] = useState(true);


  const dimension = params.get("dimension") || "composite";
  const min_pct = Number(params.get("min_pct") || 0);
  const sector = params.get("sector") || "all";
  const risk = params.get("risk") || "all";
  const q = params.get("q") || "";

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "all" || v === "0" || v === 0 || v === "") next.delete(k);
    else next.set(k, v);
    setParams(next);
  };

  useEffect(() => {
    fetchSectorList().then(setSectorList).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchScreener({ dimension, min_pct, sector, risk, q })
      .then(setRows)
      .finally(() => setLoading(false));
  }, [dimension, min_pct, sector, risk, q]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-neutral-900">Screener</h1>
        <div className="mt-2">
          <p className="text-neutral-600">Filter the full S&amp;P 500 universe by concentration dimension, threshold and sector.</p>
        </div>

        <div className="mt-8 flex flex-wrap items-end gap-4 rounded-lg border border-border bg-neutral-50 p-4">
          <Field label="Search name or ticker">
            <div className="relative w-60">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <Input
                data-testid="screener-search"
                value={q}
                onChange={(e) => set("q", e.target.value)}
                placeholder="e.g. Apple or AAPL"
                className="w-60 bg-white pl-9"
              />
            </div>
          </Field>

          <Field label="Dimension">
            <Select value={dimension} onValueChange={(v) => set("dimension", v)}>
              <SelectTrigger data-testid="filter-dimension" className="w-44 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIMENSIONS.map((d) => (
                  <SelectItem key={d.v} value={d.v} data-testid={`dimension-opt-${d.v}`}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Min %">
            <Select value={String(min_pct)} onValueChange={(v) => set("min_pct", v)} disabled={!["customer", "geographic", "product"].includes(dimension)}>
              <SelectTrigger data-testid="filter-minpct" className="w-32 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MINPCTS.map((m) => (
                  <SelectItem key={m} value={String(m)}>{m === 0 ? "Any" : `≥ ${m}%`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Sector">
            <Select value={sector} onValueChange={(v) => set("sector", v)}>
              <SelectTrigger data-testid="filter-sector" className="w-52 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sectors</SelectItem>
                {sectorList.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Risk level">
            <Select value={risk} onValueChange={(v) => set("risk", v)}>
              <SelectTrigger data-testid="filter-risk" className="w-40 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RISKS.map((r) => (
                  <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="ml-auto text-sm text-neutral-500 pb-2" data-testid="screener-count">
            {loading ? "Loading…"
              : rows.some((r) => r.locked)
                ? `${rows.filter((r) => !r.locked).length} free of ${rows.length} companies`
                : `${rows.length} companies`}
          </div>
        </div>

        <div className="mt-6">
          <CompanyTable rows={rows} />
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</label>
      {children}
    </div>
  );
}
